import { Suspense } from 'react';

import { Navigate, Outlet, Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { AppLayout } from '@/components/layouts/AppLayout';
import { DatasetQueryRedirect } from '@/components/routes/DatasetRedirect';
import { FeatureFlagGuard } from '@/components/routes/FeatureFlagGuard';
import { MarketplaceRoute } from '@/components/routes/MarketplaceRoute';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';
import { globeAuthRoutes } from '@/routes/auth.routes';

// ============================================================================
// Marketplace + landing routes: the shared globe-hosting layout group (`/`,
// search-next, the four browse pages, and the globe-hosted auth trio from
// auth.routes.tsx), the public/optional-auth detail + mission-create routes,
// and the authenticated /marketplace/* query pages. The globe layer is now the
// unified AppLayout shell's `showGlobeBackground` auth-aware host (GW-10438);
// the standalone GlobeVideoLayout module was retired.
// ============================================================================

// GW-6092: Hidden dogfooding page for gw-search-api (flag-gated, unlinked)
const SearchNextPage = lazyWithRetry(() => import('@/pages/marketplace/SearchNextPage'));
// GW-12242 (epic GW-12240): the real bare-/marketplace landing page, replacing
// the GW-9284 redirect to /marketplace/datasets.
const MarketplaceLandingPage = lazyWithRetry(() =>
  import('@/pages/marketplace/MarketplaceLandingPage').then((m) => ({
    default: m.MarketplaceLandingPage,
  }))
);
// GW-8281 (GW-8255 D25): Applications & sessions kept, reversed from an
// earlier removal plan — browse page onto SearchFacetRail + ApplicationCatalogCard.
const ApplicationBrowsePage = lazyWithRetry(() =>
  import('@/pages/marketplace/ApplicationBrowsePage').then((m) => ({
    default: m.ApplicationBrowsePage,
  }))
);
const DatasetBrowsePage = lazyWithRetry(() =>
  import('@/pages/marketplace/DatasetBrowsePage').then((m) => ({ default: m.DatasetBrowsePage }))
);
// GW-3070: RFI/Services marketplace routes
// Marketplace (optional auth)
const ServiceBrowsePage = lazyWithRetry(() =>
  import('@/pages/marketplace/ServiceBrowsePage').then((m) => ({ default: m.ServiceBrowsePage }))
);
const MissionCatalogPage = lazyWithRetry(() =>
  import('@/pages/marketplace/MissionCatalogPage').then((m) => ({ default: m.MissionCatalogPage }))
);
// GW-1694: Public marketplace pages (no auth required)
const PublicApplicationDetailPage = lazyWithRetry(
  () => import('@/pages/public/marketplace/PublicApplicationDetailPage')
);
const PublicDatasetDetailPage = lazyWithRetry(
  () => import('@/pages/public/marketplace/PublicDatasetDetailPage')
);
const ApplicationDetailPage = lazyWithRetry(() =>
  import('@/pages/protected/applications/ApplicationDetailPage').then((m) => ({
    default: m.ApplicationDetailPage,
  }))
);
// Protected Pages
const DatasetDetailPage = lazyWithRetry(() =>
  import('@/pages/marketplace/DatasetDetailPage').then((m) => ({ default: m.DatasetDetailPage }))
);
const ServiceDetailPage = lazyWithRetry(() =>
  import('@/pages/marketplace/ServiceDetailPage').then((m) => ({ default: m.ServiceDetailPage }))
);
// GW-5553: Mission detail page (skeleton, gated by missions_marketplace flag)
const MissionDetailPage = lazyWithRetry(() =>
  import('@/pages/marketplace/MissionDetailPage').then((m) => ({ default: m.MissionDetailPage }))
);
// GW-7870: Create Mission review page — briefing review + budget accept, fed
// by missionBriefingSlice (seeded from the search-next mission-lead launch).
const MissionCreatePage = lazyWithRetry(() =>
  import('@/pages/marketplace/MissionCreatePage').then((m) => ({ default: m.MissionCreatePage }))
);
// GW-9653 (demo, epic GW-9650): phone-resolution report page. Reached by
// SearchNextPage intercepting a phone-number investigation query.
const MissionDossierPage = lazyWithRetry(() => import('@/pages/mission-report/MissionDossierPage'));
// GW-10389 (demo, epic GW-9650): company-resolution report page. Reached by
// SearchNextPage intercepting a `medicaid provider "…"` investigation query.
const CompanyReportPage = lazyWithRetry(() => import('@/pages/company-report/CompanyReportPage'));
// GW-10459 (demo, epic GW-10456): company-intel rich GraphIQ profile page — a
// PARALLEL path to /company-report. Reached by SearchNextPage intercepting a
// `provider2 "…"` investigation query.
const CompanyIntelPage = lazyWithRetry(() => import('@/pages/company-intel/CompanyIntelPage'));
// GW-1343: Customer query history page
const CustomerQueryHistoryPage = lazyWithRetry(() =>
  import('@/pages/marketplace/CustomerQueryHistoryPage').then((m) => ({
    default: m.CustomerQueryHistoryPage,
  }))
);
// NOTE: also declared in publisher.routes.tsx — identical import path, so Vite
// emits a single chunk; per-file wrappers keep the route files decoupled.
// GW-1343: Query detail page
const QueryDetailPage = lazyWithRetry(() =>
  import('@/pages/datasets/QueryDetailPage').then((m) => ({ default: m.QueryDetailPage }))
);

/**
 * GW-9376: p-6 content padding for the four marketplace browse pages, which
 * used to receive it from MarketplaceLayout's `p-6` wrapper. The globe host
 * mounts the shared globe with NO content padding (SearchNextPage supplies its
 * own container padding), so the browse children opt into padding via this
 * nested pathless outlet — keeping the video node shared with `/` while
 * restoring the browse pages' original spacing in both auth states.
 */
function BrowseOutlet() {
  return (
    <div className="p-6">
      <Outlet />
    </div>
  );
}

/**
 * Public/optional-auth marketplace surface. Mounted directly under
 * RouteErrorBoundary in App.tsx.
 */
export const marketplaceRoutes = (
  <>
    {/* GW-9376/GW-10438: One auth-aware shared layout route — the unified
        AppLayout shell with `showGlobeBackground` — mounts the spinning-globe
        background video ONCE above its <Outlet /> for the whole page set that
        shares a globe within an auth state: the `/` search landing, the four
        marketplace browse pages, AND the auth pages
        (login/signup/forgot-password). Navigating among these swaps only the
        Outlet subtree, so the single <video> node persists (no restart) —
        generalizing the GW-9355 MarketplaceLayout pattern. Suspense stays
        PER-CHILD (never wrapping the layout's Outlet) so a first-visit lazy
        load never suspends and unmounts the shared video. FeatureFlagGuards
        stay on the browse children, and PublicRoute wraps ONLY the auth
        children so the redirect-if-authenticated guard is unchanged. */}
    <Route element={<AppLayout showGlobeBackground />}>
      {/* Landing page - search-next */}
      <Route
        path="/"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <SearchNextPage />
          </Suspense>
        }
      />

      {/* GW-9355/GW-9376: the four marketplace browse pages. They opt
          into p-6 content padding via BrowseOutlet (MarketplaceLayout
          used to supply it); SearchNextPage owns its own padding, so it
          is NOT nested here. FeatureFlagGuards guard the page, not the
          layout. */}
      <Route element={<BrowseOutlet />}>
        <Route
          path="/marketplace/applications"
          element={
            <FeatureFlagGuard flagName="applications_marketplace" fallbackRoute="/">
              <Suspense fallback={<PageSkeleton />}>
                <ApplicationBrowsePage />
              </Suspense>
            </FeatureFlagGuard>
          }
        />
        <Route
          path="/marketplace/datasets"
          element={
            <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/">
              <Suspense fallback={<PageSkeleton />}>
                <DatasetBrowsePage />
              </Suspense>
            </FeatureFlagGuard>
          }
        />
        <Route
          path="/marketplace/services"
          element={
            <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
              <Suspense fallback={<PageSkeleton />}>
                <ServiceBrowsePage />
              </Suspense>
            </FeatureFlagGuard>
          }
        />
        <Route
          path="/marketplace/missions"
          element={
            <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
              <Suspense fallback={<PageSkeleton />}>
                <MissionCatalogPage />
              </Suspense>
            </FeatureFlagGuard>
          }
        />
      </Route>

      {/* Auth routes (redirect if authenticated). PublicRoute wraps
          ONLY these children; the AppLayout `centered` variant provides
          the transparent, content-only centering (GW-9376/GW-10515)
          rendered above the shared globe, with the nested AuthLayout
          supplying the max-w-md card width. */}
      {globeAuthRoutes}
    </Route>

    {/* GW-1694: Public marketplace routes (auth-aware switching)
        Prototype: Listing pages use search-first LandingPage with category pre-selected;
        detail pages keep standard layout with sidebar */}
    {/* GW-8281 (GW-8255 D25): Applications & sessions kept — reversed
        from the earlier removal plan that redirected this route to
        /?cat=application. /marketplace/applications is now a
        permanent, standalone, filterable category-browse page (same
        pattern as /marketplace/{datasets,services}), matching every
        other item type per target-ia-outline.md §3.3/§12. */}
    {/* GW-9355/GW-9376: the four marketplace browse pages moved up
        into the shared globe-hosting layout route (near the `/` landing)
        so the spinning-globe background video is mounted once and persists
        across category navigation AND the landing/auth pages. */}
    {/* GW-13841: AppLayout is an ancestor layout route OUTSIDE all Suspense
        boundaries so the shell (header + sidebar) stays mounted while lazy
        page components load — preventing the full layout flash caused when
        Suspense wraps MarketplaceRoute (which previously contained AppLayout).
        GW-12242: /marketplace landing page shares this shell. */}
    <Route element={<AppLayout variant="default" />}>
      {/* GW-12242 (epic GW-12240): bare /marketplace now renders a real landing
          page — replacing the GW-9284 redirect to /marketplace/datasets. The
          four category sub-routes and their FeatureFlagGuards above are unchanged. */}
      <Route
        path="/marketplace"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <MarketplaceLandingPage />
          </Suspense>
        }
      />
      <Route
        path="/marketplace/applications/:slug"
        element={
          <FeatureFlagGuard flagName="applications_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MarketplaceRoute
                authenticatedElement={<ApplicationDetailPage />}
                publicElement={<PublicApplicationDetailPage />}
              />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-3070: Services marketplace routes (optional auth). GW-8665:
          gated behind services_marketplace — the flag governs all
          services/RFI surfaces (decision made when scoping this
          issue), and prod.json flips it to false alongside these
          guards. GW-9355: the services BROWSE route moved up into the
          shared MarketplaceLayout route; the detail route stays here. */}
      {/* GW-10552: services detail route now uses MarketplaceRoute for
          auth-aware rendering (matching /marketplace/applications/:slug and
          /marketplace/datasets/:slug), so an anonymous visitor gets a public
          view instead of the authenticated page. ServiceDetailPage already
          renders safely unauthenticated — its slug fetch hits the public,
          no-authorizer /v1/services/{slug} endpoint (GW-10374) — so it serves
          as BOTH the authenticated and public element; no separate public page
          component is needed. */}
      <Route
        path="/marketplace/services/:serviceSlug"
        element={
          <FeatureFlagGuard flagName="services_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MarketplaceRoute
                authenticatedElement={<ServiceDetailPage />}
                publicElement={<ServiceDetailPage />}
              />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      <Route
        path="/marketplace/datasets/:slug"
        element={
          <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MarketplaceRoute
                authenticatedElement={<DatasetDetailPage />}
                publicElement={<PublicDatasetDetailPage />}
              />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
      {/* GW-5553: Mission catalog browse. GW-8665: gated behind
          missions_marketplace. GW-9355: the missions BROWSE route moved
          up into the shared MarketplaceLayout route; the detail/create/
          run routes stay here. */}
      {/* GW-10552: missions detail route now uses MarketplaceRoute for
          auth-aware rendering (matching the applications/datasets detail
          routes), so an anonymous visitor gets a public view instead of the
          authenticated page. MissionDetailPage already renders safely
          unauthenticated — it fetches the public, unauthenticated
          /v1/public/marketplace/missions/{slug} route first (GW-9432),
          falling back to the authenticated taxonomy-keyed lookup — so it
          serves as BOTH the authenticated and public element; no separate
          public page component is needed. */}
      <Route
        path="/marketplace/missions/:slug"
        element={
          <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
            <Suspense fallback={<PageSkeleton />}>
              <MarketplaceRoute
                authenticatedElement={<MissionDetailPage />}
                publicElement={<MissionDetailPage />}
              />
            </Suspense>
          </FeatureFlagGuard>
        }
      />
    </Route>

    {/* GW-8027: legacy literal path with no template id — can't rebuild
        the briefing without one, so send the user back to the landing
        page to relaunch. react-router v6 ranks this literal segment above
        the :missionTypeId param route (in marketplaceProtectedRoutes), so
        it always wins over a would-be "create" match for the param. */}
    <Route path="/marketplace/missions/create" element={<Navigate to="/" replace />} />
  </>
);

/**
 * Authenticated /marketplace/* query pages (plus /mission-report, which is
 * part of the search-next flow — see the GW-9653 note below). Mounted inside
 * ProtectedRoute → TOSGuard → AppLayout in App.tsx; the guards live on that
 * shared scaffold, not here.
 */
export const marketplaceProtectedRoutes = (
  <>
    {/* GW-9653 (demo, epic GW-9650): phone-resolution report page,
        reached when SearchNextPage intercepts a phone-number
        investigation query. Authenticated (ProtectedRoute) so the
        Function URL call carries a valid Cognito token. */}
    <Route
      path="/mission-report"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <MissionDossierPage />
        </Suspense>
      }
    />
    {/* GW-10389 (demo, epic GW-9650): company-resolution report page,
        reached when SearchNextPage intercepts a `medicaid provider "…"`
        investigation query. Authenticated (ProtectedRoute) so the Function
        URL call carries a valid Cognito token. */}
    <Route
      path="/company-report"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <CompanyReportPage />
        </Suspense>
      }
    />
    {/* GW-10459 (demo, epic GW-10456): company-intel rich profile page, reached
        when SearchNextPage intercepts a `provider2 "…"` investigation query.
        Same guards as /company-report (ProtectedRoute → TOSGuard → AppLayout)
        so the Function URL call carries a valid Cognito token. */}
    <Route
      path="/company-intel"
      element={
        <Suspense fallback={<PageSkeleton />}>
          <CompanyIntelPage />
        </Suspense>
      }
    />
    {/* GW-7870/GW-8027: Create Mission review, reached from the search
        mission-lead launch or a mission detail page CTA. Parameterized by
        missionTypeId (== marketplace itemId) so the URL is shareable/
        refreshable. FeatureFlagGuard + per-child Suspense stay on the route;
        the shared ProtectedRoute → TOSGuard → AppLayout scaffold in App.tsx
        supplies the org-selection guard, TOS gate, and app chrome.
        GW-10609: moved out of AuthenticatedOnlyRoute (which requires auth but
        deliberately SKIPS org selection) into this ProtectedRoute group.
        MissionCreatePage needs org selection specifically: its token balance
        is org-scoped (useTokenBalance is enabled only when orgId != null) and
        it spends org tokens on launch, so an authed-but-no-org user under the
        old guard reached a broken/empty balance state. ProtectedRoute redirects
        such a user to /auth/select-organization; an anonymous visitor still
        redirects to /auth/login?redirect=… . TOSGuard (platform TOS
        re-acceptance) is acceptable on a token-spending flow. */}
    <Route
      path="/marketplace/missions/:missionTypeId/create"
      element={
        <FeatureFlagGuard flagName="missions_marketplace" fallbackRoute="/">
          <Suspense fallback={<PageSkeleton />}>
            <MissionCreatePage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
    {/* GW-1694: Listing and detail routes moved to public MarketplaceRoute above */}
    {/* GW-1728: Redirect old query page URLs to dataset detail page */}
    <Route
      path="/marketplace/datasets/:datasetId/query/:operationId"
      element={<DatasetQueryRedirect />}
    />
    {/* GW-1343: Customer query history page (gated by feature flag) */}
    <Route
      path="/marketplace/datasets/:datasetId/history"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/?cat=application">
          <Suspense fallback={<PageSkeleton />}>
            <CustomerQueryHistoryPage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
    {/* GW-1728: Marketplace query detail page for consumers */}
    <Route
      path="/marketplace/datasets/:datasetId/queries/:queryId"
      element={
        <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/?cat=application">
          <Suspense fallback={<PageSkeleton />}>
            <QueryDetailPage />
          </Suspense>
        </FeatureFlagGuard>
      }
    />
  </>
);
